import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { format, isSameDay, isToday, isYesterday, parseISO } from "date-fns";

import axios from "../../axios";
import { ThemeContext } from "../../context/ThemeContext";
import Title from "../../new-components/title/Title";
import HeaderInner from "../../Components/HeaderInner";
import useHostChat from "../../chat/useHostChat";
import useChatTranslations from "../../chat/useChatTranslations";
import { HostAvailability } from "../../chat/hostAvailability";
import {
  extractChatUuid,
  extractHostId,
  extractUnitName,
  getStoredChatUuid,
  setStoredChatUuid,
} from "../../chat/chatIds";
import {
  getVerificationBannerState,
  isIdentityVerifiedMessage,
  isSystemMessage,
  isVerificationRequestedMessage,
  isVerificationRevokedMessage,
} from "../../chat/verificationMessages";
import {
  getSessionTransferBannerState,
  isSessionClaimedMessage,
  isSessionTransferredMessage,
} from "../../chat/sessionTransferMessages";
import {
  getBlockedBannerState,
  isConversationBlockedMessage,
  isConversationUnblockedMessage,
} from "../../chat/blockMessages";
import {
  getResolvedBannerState,
  isConversationResolvedMessage,
  isConversationReopenedMessage,
} from "../../chat/resolveMessages";
import ReportDeviceSheet from "./ReportDeviceSheet";
import ChatSkeleton from "./ChatSkeleton";
import warningIcon from "../../assets/images/verify/warning-2.png";
import { getImageUrl } from "../../utils/media";
import useVoiceRecorder from "../../chat/useVoiceRecorder";
import VoiceRecordingBar from "./VoiceRecordingBar";
import {
  clearChatSocketLogs,
  exportChatSocketLogs,
  getChatSocketLogs,
  isChatSocketDebugEnabled,
  setChatSocketDebugEnabled,
  subscribeChatSocketDebug,
  syncChatSocketDebugFromUrl,
} from "../../chat/chatSocketDebug";
import "./HostChat.scss";

// TEST VARIABLE: Set to "verified", "pending", "error", or false (real socket data)
const TEST_VERIFICATION_STATE = false;

const parseMessageDate = (value) => {
  if (!value) return null;
  try {
    if (typeof value === "string") return parseISO(value);
    return new Date(value);
  } catch {
    return new Date(value);
  }
};

const formatDayLabel = (date, t) => {
  if (!date || Number.isNaN(date.getTime())) return "";
  if (isToday(date)) return t("Today");
  if (isYesterday(date)) return t("Yesterday");
  return format(date, "dd MMM yyyy");
};

const formatTime = (date) => {
  if (!date || Number.isNaN(date.getTime())) return "";
  return format(date, "HH:mm");
};

const REACTION_KEYS = ["HEART", "THUMBS_UP", "THUMBS_DOWN", "SMILE", "SAD"];
const REACTION_EMOJI = {
  HEART: "❤️",
  THUMBS_UP: "👍",
  THUMBS_DOWN: "👎",
  SMILE: "😊",
  SAD: "😢",
};

const MEDIA_TYPES = new Set([
  "image",
  "video",
  "audio",
  "record",
  "files",
  "file",
  "document",
]);

const pickMediaSrc = (msg) => {
  const src = msg.localPreview || msg.mediaUrl || msg.audioUrl;
  return src ? getImageUrl(src) : "";
};

const looksLikeNonImageFile = (src = "") =>
  /\.(pdf|docx?|xlsx?|pptx?|zip|txt|csv)(?:\?|#|$)/i.test(src);

const replySnippetFallback = (messageType, t) => {
  const type = String(messageType || "text").toLowerCase();
  if (type === "image") return t("Photo");
  if (type === "video") return t("Video");
  if (type === "audio" || type === "record") return t("Voice message");
  if (type === "files" || type === "file" || type === "document") {
    return t("Attachment");
  }
  return t("Message");
};

const ReplyPreview = ({ replyTo, t }) => {
  if (!replyTo) return null;
  const snippet =
    (replyTo.textSnippet || "").trim() ||
    replySnippetFallback(replyTo.messageType, t);
  const name = (replyTo.senderName || "").trim() || t("Guest");

  return (
    <div className="host-chat__reply" aria-label={t("Replying to message")}>
      <p className="host-chat__reply-name">{name}</p>
      <p className="host-chat__reply-text">{snippet}</p>
    </div>
  );
};

const MessageBody = ({
  msg,
  t,
  onImageClick,
  translatedText,
  showingOriginal,
  onToggleOriginal,
}) => {
  const type = String(msg.messageType || "text").toLowerCase();
  const src = pickMediaSrc(msg);
  const isAudio = type === "audio" || type === "record";
  const isVideo = type === "video";
  const isFile =
    type === "files" ||
    type === "file" ||
    type === "document" ||
    (type === "image" && looksLikeNonImageFile(src));

  const reply = <ReplyPreview replyTo={msg.replyTo} t={t} />;

  if (type === "image" && src && !isFile) {
    return (
      <>
        {reply}
        <div className="host-chat__media">
          <button
            type="button"
            className="host-chat__media-image-btn"
            onClick={() => onImageClick?.(src)}
            aria-label={msg.fileName || t("View image")}
          >
            <img className="host-chat__media-image" src={src} alt={msg.fileName || ""} />
          </button>
          {msg.text && msg.text !== msg.fileName ? (
            <p className="host-chat__text">{msg.text}</p>
          ) : null}
        </div>
      </>
    );
  }

  if (isVideo && src) {
    return (
      <>
        {reply}
        <div className="host-chat__media">
          <video
            className="host-chat__media-video"
            controls
            preload="metadata"
            src={src}
          />
          {msg.text && msg.text !== msg.fileName ? (
            <p className="host-chat__text">{msg.text}</p>
          ) : null}
        </div>
      </>
    );
  }

  if (isAudio && src) {
    return (
      <>
        {reply}
        <div className="host-chat__media host-chat__media--audio">
          <audio className="host-chat__media-audio" controls preload="metadata">
            <source src={src} />
          </audio>
          {msg.text && msg.text !== msg.fileName ? (
            <p className="host-chat__text">{msg.text}</p>
          ) : null}
        </div>
      </>
    );
  }

  if (isFile && src) {
    return (
      <>
        {reply}
        <div className="host-chat__media">
          <a
            className="host-chat__media-file"
            href={src}
            target="_blank"
            rel="noopener noreferrer"
          >
            <svg
              className="host-chat__media-file-icon"
              xmlns="http://www.w3.org/2000/svg"
              width="18"
              height="18"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth="2"
              aria-hidden
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M7 3h7l5 5v13a1 1 0 01-1 1H7a1 1 0 01-1-1V4a1 1 0 011-1z"
              />
              <path strokeLinecap="round" strokeLinejoin="round" d="M14 3v5h5" />
            </svg>
            <span>{msg.fileName || msg.text || t("Attachment")}</span>
          </a>
        </div>
      </>
    );
  }

  const showTranslation = Boolean(translatedText) && !showingOriginal;

  return (
    <>
      {reply}
      <p className="host-chat__text" dir="auto">
        {showTranslation ? translatedText : msg.text}
      </p>
      {translatedText ? (
        <button
          type="button"
          className="host-chat__translate-toggle"
          // Keep the tap from starting the bubble's long-press reaction picker
          onPointerDown={(e) => e.stopPropagation()}
          onClick={onToggleOriginal}
        >
          {showingOriginal ? t("Show translation") : t("Show original")}
        </button>
      ) : null}
    </>
  );
};

const HostChat = () => {
  const { t, i18n } = useTranslation();
  const { theme } = useContext(ThemeContext);
  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const lang = sessionStorage.getItem("lang") || i18n.language || "EN";
  const isArabic = String(lang).toUpperCase() === "AR";

  const room_uuid = params.room_uuid || location.pathname.split("/")[1];

  const [bootLoading, setBootLoading] = useState(true);
  const [bootError, setBootError] = useState(null);
  const [hostId, setHostId] = useState(null);
  const [unitName, setUnitName] = useState("");
  const [chatUuid, setChatUuid] = useState(() => getStoredChatUuid(room_uuid));
  const [draft, setDraft] = useState("");
  const [mediaBusy, setMediaBusy] = useState(false);
  const [mediaError, setMediaError] = useState("");
  const [previewSrc, setPreviewSrc] = useState(null);
  const [activeReactionMsgId, setActiveReactionMsgId] = useState(null);
  const longPressTimerRef = useRef(null);
  const longPressOriginRef = useRef(null);
  const pickerOpenedAtRef = useRef(0);
  const [socketDebugEnabled, setSocketDebugEnabled] = useState(() =>
    syncChatSocketDebugFromUrl(),
  );
  const [showSocketDebug, setShowSocketDebug] = useState(false);
  const [socketDebugLogs, setSocketDebugLogs] = useState(() =>
    getChatSocketLogs(),
  );
  const [expandedDebugId, setExpandedDebugId] = useState(null);
  const [debugActionMsg, setDebugActionMsg] = useState("");
  const [showReclaimSheet, setShowReclaimSheet] = useState(false);
  const [showReportSheet, setShowReportSheet] = useState(false);
  const [showReclaimSuccess, setShowReclaimSuccess] = useState(false);
  const prevReclaimAtRef = useRef(0);

  const listRef = useRef(null);
  const fileInputRef = useRef(null);
  const stickToBottomRef = useRef(true);
  const [showJumpLatest, setShowJumpLatest] = useState(false);

  const {
    isRecording,
    isPaused,
    durationLabel,
    levels,
    error: voiceError,
    clearError: clearVoiceError,
    start: startVoice,
    stop: stopVoice,
    cancel: cancelVoice,
    togglePause: toggleVoicePause,
  } = useVoiceRecorder();

  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadFooter = async () => {
      if (!room_uuid) {
        setBootError("missing_room");
        setBootLoading(false);
        return;
      }

      setBootLoading(true);
      setBootError(null);

      try {
        // Source of chat_uuid: footer/qr-code, then qr-code if needed.
        const res = await axios.get(`/${room_uuid}/footer/qr-code`, {
          headers: { lang },
        });
        const data = res?.data?.data || {};
        const resolvedHostId = extractHostId(data);
        const resolvedUnit = extractUnitName(data);
        let resolvedChatUuid =
          extractChatUuid(data) || getStoredChatUuid(room_uuid);

        if (!resolvedChatUuid) {
          try {
            const homeRes = await axios.get(`/${room_uuid}/qr-code`, {
              headers: { lang },
            });
            resolvedChatUuid = extractChatUuid(homeRes?.data?.data || {});
          } catch {
            // footer is primary; qr-code is optional fallback source
          }
        }

        if (!resolvedHostId) {
          if (!cancelled) {
            setBootError("missing_host");
            setBootLoading(false);
          }
          return;
        }

        if (!resolvedChatUuid) {
          if (!cancelled) {
            setBootError("missing_chat_uuid");
            setBootLoading(false);
          }
          return;
        }

        if (!cancelled) {
          setHostId(resolvedHostId);
          setUnitName(resolvedUnit);
          setStoredChatUuid(room_uuid, resolvedChatUuid);
          setChatUuid(resolvedChatUuid);
          if (resolvedUnit) {
            localStorage.setItem("room_number", resolvedUnit);
          }
          if (data?.host_id || data?.hostId) {
            localStorage.setItem(
              "chat_host_id",
              String(data.host_id || data.hostId),
            );
          }
          setBootLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          setBootError("footer_failed");
          setBootLoading(false);
        }
      }
    };

    loadFooter();
    return () => {
      cancelled = true;
    };
  }, [room_uuid, lang]);

  const {
    guestId,
    connectionStatus,
    connectionError,
    messages,
    reactions,
    hostOnline,
    hostName,
    hostAvatar,
    hostTyping,
    availability,
    sendText,
    sendMedia,
    sendInFlight,
    retryMessage,
    notifyTyping,
    stopTyping,
    recheckAvailability,
    disconnectSocket,
    isConnected,
    reclaimSession,
    addReaction,
    removeReaction,
    lastReclaimAt,
    reclaiming,
    reclaimError,
  } = useHostChat({
    hostId,
    // Socket param name stays `qrCode`; value must be chat_uuid (not room_uuid).
    qrCode: chatUuid,
    unitName,
    enabled: Boolean(hostId && chatUuid && !bootLoading && !bootError),
  });
  const { translations, getTranslation, isShowingOriginal, toggleOriginal } =
    useChatTranslations(messages, lang, guestId);
  const isCheckingAvailability =
    bootLoading || availability === HostAvailability.CHECKING;
  const canCompose = isConnected && !isCheckingAvailability;
  // Socket-level failures already render the "Connection lost/failed" banner.
  const showUnavailableState =
    (availability === HostAvailability.OFFLINE ||
      availability === HostAvailability.FAILED) &&
    connectionStatus !== "error" &&
    messages.length === 0;

  useEffect(() => {
    const enabled = syncChatSocketDebugFromUrl();
    setSocketDebugEnabled(enabled);
    setSocketDebugLogs(getChatSocketLogs());
    return subscribeChatSocketDebug((logs) => {
      setSocketDebugLogs(logs);
      setSocketDebugEnabled(isChatSocketDebugEnabled());
    });
  }, [location.search]);

  const flashDebugMsg = useCallback((msg) => {
    setDebugActionMsg(msg);
    setTimeout(() => setDebugActionMsg(""), 2000);
  }, []);

  const handleCopySocketDebug = useCallback(async () => {
    const text = exportChatSocketLogs();
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const ta = document.createElement("textarea");
        ta.value = text;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      flashDebugMsg("Copied");
    } catch {
      flashDebugMsg("Copy failed");
    }
  }, [flashDebugMsg]);

  const handleShareSocketDebug = useCallback(async () => {
    const text = exportChatSocketLogs();
    try {
      if (navigator.share) {
        await navigator.share({
          title: "Chat socket debug",
          text,
        });
        return;
      }
      await handleCopySocketDebug();
    } catch (err) {
      if (err?.name === "AbortError") return;
      flashDebugMsg("Share failed");
    }
  }, [flashDebugMsg, handleCopySocketDebug]);

  const handleDownloadSocketDebug = useCallback(() => {
    const text = exportChatSocketLogs();
    const blob = new Blob([text], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `chat-socket-debug-${Date.now()}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    flashDebugMsg("Downloaded");
  }, [flashDebugMsg]);

  const handleClearSocketDebug = useCallback(() => {
    clearChatSocketLogs();
    setExpandedDebugId(null);
    flashDebugMsg("Cleared");
  }, [flashDebugMsg]);

  const handleDisableSocketDebug = useCallback(() => {
    setChatSocketDebugEnabled(false);
    setSocketDebugEnabled(false);
    setShowSocketDebug(false);
    const params = new URLSearchParams(location.search);
    if (params.has("chatDebug")) {
      params.delete("chatDebug");
      const search = params.toString();
      navigate(
        { pathname: location.pathname, search: search ? `?${search}` : "" },
        { replace: true },
      );
    }
  }, [location.pathname, location.search, navigate]);

  useEffect(() => {
    if (!voiceError) return;
    if (voiceError === "unsupported") {
      setMediaError(t("Audio recording is not supported on this browser."));
    } else if (voiceError === "permission") {
      setMediaError(t("Microphone permission is required to record audio."));
    }
    clearVoiceError();
  }, [voiceError, clearVoiceError, t]);

  const scrollToBottom = useCallback((smooth = true) => {
    const el = listRef.current;
    if (!el) return;
    el.scrollTo({
      top: el.scrollHeight,
      behavior: smooth ? "smooth" : "auto",
    });
    stickToBottomRef.current = true;
    setShowJumpLatest(false);
  }, []);

  // Translations change bubble heights too, so they also keep the list pinned
  useEffect(() => {
    if (stickToBottomRef.current) {
      scrollToBottom(false);
    }
  }, [messages, hostTyping, translations, scrollToBottom]);

  const cancelLongPress = useCallback(() => {
    clearTimeout(longPressTimerRef.current);
    longPressOriginRef.current = null;
  }, []);

  const openReactionPicker = useCallback((msgId) => {
    if (!msgId || String(msgId).startsWith("temp:") || String(msgId).startsWith("__")) return;
    pickerOpenedAtRef.current = Date.now();
    setActiveReactionMsgId(msgId);
  }, []);

  const startLongPress = useCallback((msgId, event) => {
    if (!msgId || String(msgId).startsWith("temp:") || String(msgId).startsWith("__")) return;
    cancelLongPress();
    longPressOriginRef.current = { x: event.clientX, y: event.clientY };
    longPressTimerRef.current = setTimeout(() => {
      longPressOriginRef.current = null;
      openReactionPicker(msgId);
    }, 150);
  }, [cancelLongPress, openReactionPicker]);

  const onLongPressMove = useCallback((event) => {
    const origin = longPressOriginRef.current;
    if (!origin) return;
    const dx = event.clientX - origin.x;
    const dy = event.clientY - origin.y;
    if (dx * dx + dy * dy > 100) cancelLongPress();
  }, [cancelLongPress]);

  const handleToggleReaction = useCallback(
    (msgId, key) => {
      if (!msgId || !key) return;
      if (String(msgId).startsWith("temp:") || String(msgId).startsWith("__")) return;
      const msgReactions = reactions?.[msgId] || {};
      if (msgReactions[key]?.mine) {
        removeReaction(msgId, key);
      } else {
        addReaction(msgId, key);
      }
      setActiveReactionMsgId(null);
    },
    [reactions, addReaction, removeReaction],
  );

  // Close the picker when tapping anywhere outside it
  useEffect(() => {
    if (!activeReactionMsgId) return undefined;
    const onDocPointerDown = (e) => {
      if (e.target.closest?.(".host-chat__reaction-picker")) return;
      // Ignore the tap that happens right after long-press opened the picker
      if (Date.now() - pickerOpenedAtRef.current < 300) return;
      setActiveReactionMsgId(null);
    };
    document.addEventListener("pointerdown", onDocPointerDown);
    return () => document.removeEventListener("pointerdown", onDocPointerDown);
  }, [activeReactionMsgId]);

  const onListScroll = () => {
    const el = listRef.current;
    if (!el) return;
    const distance = el.scrollHeight - el.scrollTop - el.clientHeight;
    const atBottom = distance < 80;
    stickToBottomRef.current = atBottom;
    setShowJumpLatest(!atBottom);
  };

  const rows = useMemo(() => {
    const GROUP_THRESHOLD_MS = 1 * 60 * 1000; // 2 minutes
    const items = [];
    let lastDay = null;

    messages.forEach((msg) => {
      const date = parseMessageDate(msg.createdAt);
      if (date && (!lastDay || !isSameDay(lastDay, date))) {
        items.push({
          type: "separator",
          id: `day-${date.toISOString().slice(0, 10)}`,
          label: formatDayLabel(date, t),
        });
        lastDay = date;
      }
      items.push({ type: "message", ...msg, date });
    });

    // Annotate each message row with grouping flags:
    // isGrouped   – not the first message in its group (reduce top spacing)
    // isLastInGroup – last message in its group (show timestamp + avatar)
    for (let i = 0; i < items.length; i++) {
      const row = items[i];
      if (row.type !== "message") continue;

      const prev = items.slice(0, i).reverse().find((r) => r.type === "message");
      const next = items.slice(i + 1).find((r) => r.type === "message");

      const sameSenderAsPrev =
        prev &&
        !isSystemMessage(prev) &&
        !isSystemMessage(row) &&
        row.isMine === prev.isMine;

      const sameSenderAsNext =
        next &&
        !isSystemMessage(next) &&
        !isSystemMessage(row) &&
        row.isMine === next.isMine;

      const withinThresholdPrev =
        sameSenderAsPrev &&
        row.date &&
        prev.date &&
        Math.abs(row.date - prev.date) < GROUP_THRESHOLD_MS;

      const withinThresholdNext =
        sameSenderAsNext &&
        row.date &&
        next.date &&
        Math.abs(next.date - row.date) < GROUP_THRESHOLD_MS;

      row.isGrouped = Boolean(withinThresholdPrev);
      row.isLastInGroup = !withinThresholdNext;
    }

    return items;
  }, [messages, t]);

  const verificationBanner = useMemo(() => {
    if (TEST_VERIFICATION_STATE === "verified") {
      return { type: "verified", unitName: "Villa 12" };
    }
    if (TEST_VERIFICATION_STATE === "pending") {
      return { type: "pending" };
    }
    return getVerificationBannerState(messages);
  }, [messages]);

  const sessionBanner = useMemo(
    () => getSessionTransferBannerState(messages, lastReclaimAt),
    [messages, lastReclaimAt],
  );

  const blockedBanner = useMemo(() => getBlockedBannerState(messages), [messages]);
  const isBlocked = Boolean(blockedBanner);

  const resolvedBanner = useMemo(
    () => getResolvedBannerState(messages),
    [messages],
  );
  const isResolved = Boolean(resolvedBanner);

  // Chat is dead once the host blocks or resolves it — tear down the socket
  // so we don't flash a "connection lost" banner over a closed conversation.
  useEffect(() => {
    if (isBlocked || isResolved) disconnectSocket();
  }, [isBlocked, isResolved, disconnectSocket]);

  const chatLocked = Boolean(sessionBanner) || isBlocked || isResolved;

  useEffect(() => {
    if (!lastReclaimAt || lastReclaimAt === prevReclaimAtRef.current) return;
    prevReclaimAtRef.current = lastReclaimAt;
    setShowReclaimSheet(false);
    setShowReclaimSuccess(true);
    const timer = setTimeout(() => setShowReclaimSuccess(false), 4000);
    return () => clearTimeout(timer);
  }, [lastReclaimAt]);

  const testRows = useMemo(() => {
    if (
      TEST_VERIFICATION_STATE !== "verified" &&
      TEST_VERIFICATION_STATE !== "pending"
    ) {
      return rows;
    }

    // Add mock system message only when explicitly testing UI
    const mockMsg = {
      type: "message",
      id: "mock-system-msg",
      isSystem: true,
      text:
        TEST_VERIFICATION_STATE === "verified"
          ? "identity_verified"
          : "verification_requested",
      messageType: "system",
      date: new Date(),
    };

    return [...rows, mockMsg];
  }, [rows]);

  const handleSend = (e) => {
    e?.preventDefault?.();
    if (chatLocked) return;
    const text = draft.trim();
    if (!text || !canCompose || sendInFlight || mediaBusy) return;
    const ok = sendText(text);
    if (ok) {
      setDraft("");
      stickToBottomRef.current = true;
      scrollToBottom(true);
    }
  };

  const onDraftChange = (e) => {
    setDraft(e.target.value);
    if (e.target.value.trim()) {
      notifyTyping();
    } else {
      stopTyping();
    }
  };

  const handlePickFile = () => {
    if (chatLocked || !canCompose || sendInFlight || mediaBusy || isRecording) return;
    setMediaError("");
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    setMediaBusy(true);
    setMediaError("");
    stickToBottomRef.current = true;
    const ok = await sendMedia(file);
    setMediaBusy(false);
    if (!ok) {
      setMediaError(t("Could not send attachment. Please try again."));
      return;
    }
    scrollToBottom(true);
  };

  const startRecording = async () => {
    if (chatLocked || !canCompose || sendInFlight || mediaBusy || isRecording) return;
    setMediaError("");
    await startVoice();
  };

  const sendRecording = async () => {
    if (mediaBusy || sendInFlight) return;
    const file = await stopVoice();
    if (!file) return;

    setMediaBusy(true);
    stickToBottomRef.current = true;
    const ok = await sendMedia(file, { messageType: "record" });
    setMediaBusy(false);
    if (!ok) {
      setMediaError(t("Could not send voice message. Please try again."));
      return;
    }
    scrollToBottom(true);
  };

  const cancelRecording = () => {
    cancelVoice();
  };

  const statusLabel = (() => {
    if (isBlocked) return t("Blocked");
    if (isResolved) return t("Resolved");
    if (sessionBanner?.type === "claimed") return t("Session inactive");
    if (isCheckingAvailability) return t("Connecting...");
    if (connectionStatus === "reconnecting") return t("Reconnecting...");
    if (connectionStatus === "error") {
      return connectionError === "connection_lost"
        ? t("Connection lost")
        : t("Connection failed");
    }
    if (hostOnline) return t("Host online");
    return t("Host offline");
  })();

  const statusDotClass = (() => {
    if (isBlocked) return "is-blocked";
    if (isResolved) return "is-resolved";
    if (sessionBanner?.type === "claimed") return "is-offline";
    if (isCheckingAvailability) return "is-offline";
    if (connectionStatus === "connected" && hostOnline) return "is-online";
    if (connectionStatus === "connected") return "is-away";
    return "is-offline";
  })();

  const handleConfirmReclaim = async () => {
    const ok = await reclaimSession();
    if (!ok) return;
  };

  const renderHeader = () => {
    return (
      <header className="host-chat__header">
        <button
          type="button"
          className="host-chat__header-back"
          onClick={() => navigate(`/${room_uuid}/qr-code`)}
          aria-label={t("back")}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <div className="host-chat__header-info">
          <div className="host-chat__header-avatar">
            {hostAvatar ? (
              <img src={hostAvatar} alt={hostName || "Host"} />
            ) : (
              <span>{(hostName || unitName || "H")[0].toUpperCase()}</span>
            )}
          </div>
          <div className="host-chat__header-text">
            <h2>{unitName || t("Chat with Host")}</h2>
            <div
              className={`host-chat__header-status ${
                isBlocked ? "is-blocked" : isResolved ? "is-resolved" : ""
              }`}
            >
              <span
                className={`host-chat__dot ${statusDotClass}`}
              />
              <span>{statusLabel}</span>
            </div>
          </div>
        </div>
      </header>
    );
  };

  const isErrorState = bootError || TEST_VERIFICATION_STATE === "error";

  if (isErrorState) {
    const errorTitle = t("Oh no!");
    const defaultErrorMsg = t("We couldn't complete your request.\nPlease try again.");
    let errorText = defaultErrorMsg;
    
    if (bootError === "missing_host") {
      errorText = t("Host chat is not available for this room yet.");
    } else if (bootError === "missing_chat_uuid") {
      errorText = t("Unable to open chat. Please try again.");
    } else if (bootError && bootError !== "error") {
      errorText = t("Unable to open chat. Please try again.");
    }

    return (
      <div className={`host-chat ${isArabic ? "arabic" : ""}`}>
        {renderHeader()}
        <main className="host-chat__main">
          <div className="host-chat__state host-chat__state--full-error">
            <div className="host-chat__error-icon-wrapper">
              <img src={warningIcon} alt="Error" className="host-chat__error-icon" />
            </div>
            <h2 className="host-chat__error-title">{errorTitle}</h2>
            <p className="host-chat__error-desc">{errorText}</p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className={`host-chat ${isArabic ? "arabic" : ""}`}>
      {renderHeader()}

      <main className="host-chat__main">
        {isBlocked ? (
          <div className="host-chat__connection-lost-banner is-lost">
            <div className="host-chat__connection-lost-banner-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="10" fill="#EF4444"/>
                <path d="M8 12H16" stroke="white" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </div>
            <div className="host-chat__connection-lost-banner-copy">
              <p>
                {t(
                  "This conversation has been blocked by the host. You can no longer send or receive messages in this chat.",
                )}
              </p>
              <div className="host-chat__connection-lost-banner-actions">
                <button
                  type="button"
                  className="host-chat__pill-btn is-primary"
                  onClick={() => window.location.reload()}
                >
                  {t("Reload")}
                </button>
              </div>
            </div>
          </div>
        ) : isResolved ? (
          <div className="host-chat__connection-lost-banner is-resolved">
            <div className="host-chat__connection-lost-banner-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="10" fill="#22C55E"/>
                <path d="M8 12L11 15L16 9" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <div className="host-chat__connection-lost-banner-copy">
              <p>
                {t(
                  "This conversation has been resolved by the host, you can no longer send or receive messages in this chat.",
                )}
              </p>
            </div>
          </div>
        ) : (
          connectionStatus === "error" && (
            <div
              className={`host-chat__connection-lost-banner ${
                connectionError === "connection_lost" ? "is-lost" : "is-failed"
              }`}
            >
              <div className="host-chat__connection-lost-banner-icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" fill="#EF4444"/>
                  <path d="M12 8V13" stroke="white" strokeWidth="2" strokeLinecap="round"/>
                  <circle cx="12" cy="16.5" r="1.25" fill="white"/>
                </svg>
              </div>
              <div className="host-chat__connection-lost-banner-copy">
                <h3>
                  {connectionError === "connection_lost"
                    ? t("Connection lost")
                    : t("Connection failed")}
                </h3>
                <p>
                  {connectionError === "connection_lost"
                    ? t(
                        "Your chat connection was interrupted. Please reload the page to continue messaging.",
                      )
                    : connectionError === "connection_error"
                      ? t("Unable to connect to chat.")
                      : connectionError || t("Unable to connect to chat.")}
                </p>
                <div className="host-chat__connection-lost-banner-actions">
                  <button
                    type="button"
                    className="host-chat__pill-btn is-primary"
                    onClick={() => window.location.reload()}
                  >
                    {t("Reload")}
                  </button>
                </div>
              </div>
            </div>
          )
        )}

        {showReclaimSuccess && (
          <div className="host-chat__success-banner">
            <div className="host-chat__success-banner-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="10" fill="#22C55E"/>
                <path d="M8 12L11 15L16 9" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <div className="host-chat__success-banner-copy">
              <h3>{t("Session reclaimed successfully")}</h3>
            </div>
          </div>
        )}

        {!showReclaimSuccess && sessionBanner?.type === "transferred" && (
          <div className="host-chat__reclaim-banner">
            <div className="host-chat__reclaim-banner-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="10" fill="#FFC72B"/>
                <path d="M12 8V12" stroke="white" strokeWidth="2" strokeLinecap="round"/>
                <circle cx="12" cy="16" r="1" fill="white"/>
              </svg>
            </div>
            <div className="host-chat__reclaim-banner-copy">
              <h3>{t("Active on Another Device!")}</h3>
              <p>
                {t(
                  "A new chat has been started from another device. Your current session is no longer active. You can reclaim this session or report an issue if you believe this was unexpected.",
                )}
              </p>
              <div className="host-chat__reclaim-banner-actions">
                <button
                  type="button"
                  className="host-chat__pill-btn is-primary"
                  onClick={() => setShowReclaimSheet(true)}
                  disabled={reclaiming}
                >
                  {t("Reclaim session")}
                </button>
                <button
                  type="button"
                  className="host-chat__pill-btn is-secondary"
                  onClick={() => setShowReportSheet(true)}
                  disabled={reclaiming}
                >
                  {t("Report")}
                </button>
              </div>
            </div>
          </div>
        )}

        {!showReclaimSuccess && sessionBanner?.type === "claimed" && (
          <div className="host-chat__claimed-banner">
            <div className="host-chat__claimed-banner-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="10" fill="#FFC72B"/>
                <path d="M12 8V12" stroke="white" strokeWidth="2" strokeLinecap="round"/>
                <circle cx="12" cy="16" r="1" fill="white"/>
              </svg>
            </div>
            <div className="host-chat__claimed-banner-copy">
              <p>
                {t(
                  "This conversation has been claimed by another device, you can no longer send or receive messages in this chat.",
                )}
              </p>
            </div>
          </div>
        )}

        {verificationBanner?.type === "pending" && (
          <div className="host-chat__verify-banner is-pending">
            <div className="host-chat__verify-banner-icon is-pending">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M12 22C12 22 20 18 20 12V5L12 2L4 5V12C4 18 12 22 12 22Z" fill="#FFC72B"/>
                <path d="M12 10.5C12.8284 10.5 13.5 9.82843 13.5 9C13.5 8.17157 12.8284 7.5 12 7.5C11.1716 7.5 10.5 8.17157 10.5 9C10.5 9.82843 11.1716 10.5 12 10.5Z" fill="white"/>
                <path d="M11 10.5H13V15.5H11V10.5Z" fill="white"/>
              </svg>
            </div>
            <div className="host-chat__verify-banner-copy">
              <h3>{t("Identity verification requested")}</h3>
              <p>
                {t(
                  "For security, the host requires identity verification. Check your email or mobile and click on the link to verify."
                )}
              </p>
            </div>
          </div>
        )}

        {verificationBanner?.type === "verified" && (
          <div className="host-chat__verify-banner is-verified">
            <div className="host-chat__verify-banner-icon is-success">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="10" fill="#22C55E"/>
                <path d="M8 12L11 15L16 9" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <div className="host-chat__verify-banner-copy">
              <h3>{t("Identity Verified!")}</h3>
              <p>
                {t("You're verified as a guest in")}{" "}
                {verificationBanner.unitName || unitName || t("this room")}.
              </p>
            </div>
          </div>
        )}

        <div
          className="host-chat__messages"
          ref={listRef}
          onScroll={onListScroll}
        >
          {isCheckingAvailability && messages.length === 0 && (
            <ChatSkeleton label={t("Connecting...")} />
          )}

          {showUnavailableState && (
            <div className="host-chat__state host-chat__state--inline">
              <p>
                {availability === HostAvailability.OFFLINE
                  ? t("The host is currently offline. Please try again shortly.")
                  : t("Could not load messages.")}
              </p>
              <button
                type="button"
                className="reg-btn"
                onClick={recheckAvailability}
              >
                {t("Try Again")}
              </button>
            </div>
          )}

          {availability === HostAvailability.AVAILABLE &&
            testRows.length === 0 &&
            isConnected && (
              <div className="host-chat__state host-chat__state--inline">
                <p>{t("No messages yet. Say hello to your host.")}</p>
              </div>
            )}

          {testRows.map((row) => {
            if (row.type === "separator") {
              return (
                <div key={row.id} className="host-chat__day">
                  <span>{row.label}</span>
                </div>
              );
            }

            if (isSystemMessage(row)) {
              const isPending = row.text === "verification_requested" || isVerificationRequestedMessage(row);
              const isVerified = row.text === "identity_verified" || isIdentityVerifiedMessage(row);
              const isRevoked =
                row.text === "verification_revoked" || isVerificationRevokedMessage(row);

              if (isSessionClaimedMessage(row)) {
                return null;
              }

              if (isConversationBlockedMessage(row)) {
                return (
                  <div key={row.id} className="host-chat__system">
                    <div className="host-chat__system-pill-large is-blocked">
                      <div className="host-chat__system-pill-icon">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none">
                          <circle cx="12" cy="12" r="10" fill="#EF4444"/>
                          <path d="M8 12H16" stroke="white" strokeWidth="2" strokeLinecap="round"/>
                        </svg>
                      </div>
                      <span>{row.text}</span>
                    </div>
                  </div>
                );
              }

              if (isConversationUnblockedMessage(row)) {
                return (
                  <div key={row.id} className="host-chat__system">
                    <div className="host-chat__system-pill-large is-verified">
                      <div className="host-chat__system-pill-icon">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none">
                          <circle cx="12" cy="12" r="10" fill="#22C55E"/>
                          <path d="M8 12L11 15L16 9" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </div>
                      <span>{row.text}</span>
                    </div>
                  </div>
                );
              }

              if (isConversationResolvedMessage(row)) {
                return (
                  <div key={row.id} className="host-chat__system">
                    <div className="host-chat__system-pill-large is-resolved">
                      <div className="host-chat__system-pill-icon">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none">
                          <circle cx="12" cy="12" r="10" fill="#22C55E"/>
                          <path d="M8 12L11 15L16 9" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </div>
                      <span>{row.text}</span>
                    </div>
                  </div>
                );
              }

              if (isConversationReopenedMessage(row)) {
                return (
                  <div key={row.id} className="host-chat__system">
                    <div className="host-chat__system-pill-large is-verified">
                      <div className="host-chat__system-pill-icon">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none">
                          <circle cx="12" cy="12" r="10" fill="#22C55E"/>
                          <path d="M8 12L11 15L16 9" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </div>
                      <span>{row.text}</span>
                    </div>
                  </div>
                );
              }

              if (isRevoked) {
                const reason =
                  (row.note && String(row.note).trim()) ||
                  t("New device detected");
                return (
                  <div key={row.id} className="host-chat__system">
                    <div className="host-chat__system-pill-large is-revoked">
                      <div className="host-chat__system-pill-icon">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none">
                          <circle cx="12" cy="12" r="10" fill="#6B7280"/>
                          <path d="M12 8V13" stroke="white" strokeWidth="2" strokeLinecap="round"/>
                          <circle cx="12" cy="16.5" r="1.25" fill="white"/>
                        </svg>
                      </div>
                      <span>
                        {t("Verification revoked")} · {reason}
                      </span>
                    </div>
                  </div>
                );
              }

              if (isPending || isVerified) {
                return (
                  <div key={row.id} className="host-chat__system">
                    <div
                      className={`host-chat__system-pill-large ${
                        isVerified ? "is-verified" : "is-pending"
                      }`}
                    >
                      <div className="host-chat__system-pill-icon">
                        {isVerified ? (
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none">
                            <circle cx="12" cy="12" r="10" fill="#22C55E"/>
                            <path d="M8 12L11 15L16 9" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        ) : (
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none">
                            <path d="M12 22C12 22 20 18 20 12V5L12 2L4 5V12C4 18 12 22 12 22Z" fill="#666666"/>
                            <path d="M12 10.5C12.8284 10.5 13.5 9.82843 13.5 9C13.5 8.17157 12.8284 7.5 12 7.5C11.1716 7.5 10.5 8.17157 10.5 9C10.5 9.82843 11.1716 10.5 12 10.5Z" fill="white"/>
                            <path d="M11 10.5H13V15.5H11V10.5Z" fill="white"/>
                          </svg>
                        )}
                      </div>
                      <span>
                        {isVerified
                          ? t("Identity Verified!")
                          : t("Verification Requested by Host")}
                      </span>
                    </div>
                  </div>
                );
              }

              return (
                <div key={row.id} className="host-chat__system">
                  <div
                    className={`host-chat__system-pill ${
                      isSessionTransferredMessage(row) ? "is-session-transfer" : ""
                    }`}
                  >
                    <span>{row.text}</span>
                  </div>
                </div>
              );
            }

            const mine = row.isMine;
            const seen = row.isSeen || row.status === "seen";
            const showTime = row.isLastInGroup !== false;
            const hasMineStatus =
              mine &&
              (row.status === "pending" || row.status === "failed" || showTime);
            const showMeta = showTime || hasMineStatus;
            const msgReactions = reactions?.[row.id] || {};
            const reactionEntries = Object.entries(msgReactions).filter(([, v]) => v.count > 0);
            const isPickerOpen = activeReactionMsgId === row.id;
            return (
              <div
                key={row.id}
                className={`host-chat__bubble-row ${mine ? "is-mine" : "is-host"}${row.isGrouped ? " is-grouped" : ""}`}
              >
                {!mine && (
                  <div className="host-chat__bubble-avatar">
                    {row.isLastInGroup !== false ? (
                      hostAvatar ? (
                        <img src={hostAvatar} alt="H" />
                      ) : (
                        <span>{(row.senderName || hostName || "H")[0].toUpperCase()}</span>
                      )
                    ) : null}
                  </div>
                )}
                <div
                  className={`host-chat__bubble-wrapper${isPickerOpen ? " has-picker" : ""}`}
                >
                  {isPickerOpen && (
                    <div
                      className={`host-chat__reaction-picker ${mine ? "is-mine" : "is-host"}`}
                      onPointerDown={(e) => e.stopPropagation()}
                    >
                      {REACTION_KEYS.map((key) => (
                        <button
                          key={key}
                          type="button"
                          className={`host-chat__reaction-picker-btn${msgReactions[key]?.mine ? " is-active" : ""}`}
                          onPointerDown={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleToggleReaction(row.id, key);
                          }}
                          aria-label={key}
                        >
                          {REACTION_EMOJI[key]}
                        </button>
                      ))}
                    </div>
                  )}
                  <div
                    className={`host-chat__bubble ${
                      row.status === "failed" ? "is-failed" : ""
                    } ${row.status === "pending" ? "is-pending" : ""} ${
                      MEDIA_TYPES.has(
                        String(row.messageType || "").toLowerCase()
                      )
                        ? "has-media"
                        : ""
                    }`}
                    onContextMenu={(e) => {
                      e.preventDefault();
                      openReactionPicker(row.id);
                    }}
                    onPointerDown={(e) => startLongPress(row.id, e)}
                    onPointerUp={cancelLongPress}
                    onPointerMove={onLongPressMove}
                    onPointerLeave={cancelLongPress}
                    onPointerCancel={cancelLongPress}
                  >
                    <MessageBody
                      msg={row}
                      t={t}
                      onImageClick={setPreviewSrc}
                      translatedText={getTranslation(row)}
                      showingOriginal={isShowingOriginal(row.id)}
                      onToggleOriginal={() => toggleOriginal(row.id)}
                    />
                  </div>
                  {reactionEntries.length > 0 && (
                    <div className={`host-chat__reactions ${mine ? "is-mine" : "is-host"}`}>
                      {reactionEntries.map(([key, val]) => (
                        <button
                          key={key}
                          type="button"
                          className={`host-chat__reaction-chip${val.mine ? " is-mine" : ""}`}
                          onClick={() => handleToggleReaction(row.id, key)}
                          aria-label={`${REACTION_EMOJI[key]} ${val.count}`}
                        >
                          <span className="host-chat__reaction-chip-emoji">{REACTION_EMOJI[key]}</span>
                          {val.count > 1 && (
                            <span className="host-chat__reaction-chip-count">{val.count}</span>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                  {showMeta && (
                    <div className="host-chat__meta">
                      {showTime && <span>{formatTime(row.date)}</span>}
                      {mine && row.status === "pending" ? (
                        <span>{t("Sending...")}</span>
                      ) : mine && row.status === "failed" ? (
                        <button
                          type="button"
                          className="host-chat__link-btn"
                          onClick={() => retryMessage(row.tempId)}
                        >
                          {t("Retry")}
                        </button>
                      ) : mine && row.status !== "failed" && row.status !== "pending" ? (
                        <span className="host-chat__ticks">
                          {seen ? "✓✓" : "✓"}
                        </span>
                      ) : null}
                    </div>
                  )}
                </div>
              </div>
            );
          })}

          {hostTyping && !chatLocked && (
            <div className="host-chat__bubble-row is-host">
              <div className="host-chat__bubble-avatar">
                {hostAvatar ? (
                  <img src={hostAvatar} alt="H" />
                ) : (
                  <span>{(hostName || "H")[0].toUpperCase()}</span>
                )}
              </div>
              <div className="host-chat__bubble-wrapper">
                <div className="host-chat__bubble host-chat__typing">
                  <span />
                  <span />
                  <span />
                </div>
              </div>
            </div>
          )}
        </div>

        {showJumpLatest && (
          <button
            type="button"
            className="host-chat__jump"
            onClick={() => scrollToBottom(true)}
          >
            {t("Latest messages")}
          </button>
        )}

        {mediaError ? (
          <div className="host-chat__media-error">{mediaError}</div>
        ) : null}

        {isResolved ? null : chatLocked ? (
          <div className="host-chat__composer-locked">
            <p>{t("Messaging is closed.")}</p>
          </div>
        ) : isRecording ? (
          <div className="host-chat__composer host-chat__composer--recording">
            <VoiceRecordingBar
              levels={levels}
              durationLabel={durationLabel}
              isPaused={isPaused}
              disabled={mediaBusy || sendInFlight}
              onCancel={cancelRecording}
              onTogglePause={toggleVoicePause}
              onSend={sendRecording}
            />
          </div>
        ) : (
        <form className="host-chat__composer" onSubmit={handleSend}>
          <input
            ref={fileInputRef}
            type="file"
            className="host-chat__file-input"
            accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.zip,.csv"
            onChange={handleFileChange}
            hidden
          />
          <div className="host-chat__input-wrapper">
            <input
              type="text"
              className="host-chat__input"
              value={draft}
              onChange={onDraftChange}
              onBlur={stopTyping}
              placeholder={t("Message ") + (unitName || "") + "..."}
              disabled={!canCompose || mediaBusy}
              autoComplete="off"
              enterKeyHint="send"
            />
            <button
              type="button"
              className="host-chat__attachment"
              aria-label={t("Attach")}
              onClick={handlePickFile}
              disabled={!canCompose || sendInFlight || mediaBusy}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
              </svg>
            </button>
          </div>
          {draft.trim() ? (
            <button
              type="submit"
              className="host-chat__send"
              disabled={!canCompose || sendInFlight || mediaBusy}
              aria-label={t("Send")}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="currentColor"
                aria-hidden="true"
              >
                <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
              </svg>
            </button>
          ) : (
            <button
              type="button"
              className="host-chat__send"
              disabled={!canCompose || sendInFlight || mediaBusy}
              aria-label={t("Voice")}
              onClick={startRecording}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="currentColor"
                aria-hidden="true"
              >
                <path d="M12 14c1.66 0 2.99-1.34 2.99-3L15 5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm5.3-3c0 3-2.54 5.1-5.3 5.1S6.7 14 6.7 11H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c3.28-.48 6-3.3 6-6.72h-1.7z" />
              </svg>
            </button>
          )}
        </form>
        )}

        <ReportDeviceSheet
          open={showReportSheet}
          onClose={() => setShowReportSheet(false)}
          roomUuid={room_uuid}
          sessionId={chatUuid}
          lang={lang}
        />

        {showReclaimSheet && (
          <div
            className="host-chat__reclaim-overlay"
            role="presentation"
            onClick={() => {
              if (!reclaiming) setShowReclaimSheet(false);
            }}
          >
            <div
              className="host-chat__reclaim-sheet"
              role="dialog"
              aria-modal="true"
              aria-labelledby="reclaim-sheet-title"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                className="host-chat__reclaim-sheet-close"
                aria-label={t("Close")}
                disabled={reclaiming}
                onClick={() => setShowReclaimSheet(false)}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M18 6L6 18M6 6l12 12" />
                </svg>
              </button>

              <div className="host-chat__reclaim-sheet-icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none">
                  <rect x="3" y="4" width="18" height="14" rx="2" stroke="#172449" strokeWidth="1.8"/>
                  <path d="M8 20h8" stroke="#172449" strokeWidth="1.8" strokeLinecap="round"/>
                  <path d="M12 18v2" stroke="#172449" strokeWidth="1.8" strokeLinecap="round"/>
                </svg>
              </div>

              <h3 id="reclaim-sheet-title" className="host-chat__reclaim-sheet-title">
                {t("Reclaim Session?")}
              </h3>
              <p className="host-chat__reclaim-sheet-desc">
                {t(
                  "This will transfer the conversation back to this device and disconnect the other device.",
                )}
              </p>

              {reclaimError ? (
                <div className="host-chat__reclaim-sheet-error">
                  {reclaimError === "not_connected"
                    ? t("Unable to connect to chat.")
                    : reclaimError === "reclaim_failed"
                      ? t("Could not reclaim session. Please try again.")
                      : String(reclaimError)}
                </div>
              ) : null}

              <div className="host-chat__reclaim-sheet-actions">
                <button
                  type="button"
                  className="host-chat__sheet-btn is-secondary"
                  disabled={reclaiming}
                  onClick={() => setShowReclaimSheet(false)}
                >
                  {t("Dismiss")}
                </button>
                <button
                  type="button"
                  className="host-chat__sheet-btn is-primary"
                  disabled={reclaiming}
                  onClick={handleConfirmReclaim}
                >
                  {reclaiming ? t("Reclaiming...") : t("Yes, Reclaim")}
                </button>
              </div>
            </div>
          </div>
        )}
      </main>

      {previewSrc && (
        <div
          className="host-chat__image-preview"
          role="dialog"
          aria-modal="true"
          aria-label={t("Image preview")}
          onClick={() => setPreviewSrc(null)}
        >
          <button
            type="button"
            className="host-chat__image-preview-close"
            onClick={() => setPreviewSrc(null)}
            aria-label={t("Close")}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
            </svg>
          </button>
          <img
            className="host-chat__image-preview-img"
            src={previewSrc}
            alt={t("Preview")}
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      {socketDebugEnabled && (
        <>
          <button
            type="button"
            className={`host-chat__debug-fab ${isConnected ? "is-connected" : "is-disconnected"}`}
            onClick={() => setShowSocketDebug(true)}
            aria-label="Socket Debug"
          >
            <span className="host-chat__debug-fab-dot" />
            Debug
          </button>

          {showSocketDebug && (
            <div className="host-chat__debug-panel" role="dialog" aria-modal="true" aria-label="Socket Debug">
              <div className="host-chat__debug-panel-header">
                <div>
                  <h3>Socket Debug</h3>
                  <p>
                    {isConnected ? "connected" : connectionStatus || "disconnected"}
                    {" · "}
                    {socketDebugLogs.length} events
                  </p>
                </div>
                <button
                  type="button"
                  className="host-chat__debug-panel-close"
                  onClick={() => setShowSocketDebug(false)}
                  aria-label="Close"
                >
                  ×
                </button>
              </div>

              <div className="host-chat__debug-panel-actions">
                <button type="button" onClick={handleCopySocketDebug}>Copy</button>
                <button type="button" onClick={handleShareSocketDebug}>Share</button>
                <button type="button" onClick={handleDownloadSocketDebug}>Download</button>
                <button type="button" onClick={handleClearSocketDebug}>Clear</button>
                <button type="button" className="is-danger" onClick={handleDisableSocketDebug}>
                  Off
                </button>
              </div>

              {debugActionMsg ? (
                <div className="host-chat__debug-panel-toast">{debugActionMsg}</div>
              ) : null}

              <div className="host-chat__debug-panel-list">
                {socketDebugLogs.length === 0 ? (
                  <p className="host-chat__debug-panel-empty">No socket events yet.</p>
                ) : (
                  [...socketDebugLogs].reverse().map((entry) => {
                    const open = expandedDebugId === entry.id;
                    const time = entry.ts
                      ? new Date(entry.ts).toLocaleTimeString()
                      : "";
                    return (
                      <button
                        key={entry.id}
                        type="button"
                        className={`host-chat__debug-row is-${entry.direction}`}
                        onClick={() =>
                          setExpandedDebugId(open ? null : entry.id)
                        }
                      >
                        <div className="host-chat__debug-row-top">
                          <span className="host-chat__debug-row-time">{time}</span>
                          <span className="host-chat__debug-row-dir">
                            {String(entry.direction || "sys").toUpperCase()}
                          </span>
                          <span className="host-chat__debug-row-event">{entry.event}</span>
                        </div>
                        {open ? (
                          <pre className="host-chat__debug-row-payload">
                            {JSON.stringify(
                              {
                                payload: entry.payload,
                                meta: entry.meta,
                              },
                              null,
                              2,
                            )}
                          </pre>
                        ) : (
                          <div className="host-chat__debug-row-preview">
                            {typeof entry.payload === "string"
                              ? entry.payload.slice(0, 120)
                              : JSON.stringify(entry.payload)?.slice(0, 120)}
                          </div>
                        )}
                      </button>
                    );
                  })
                )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default HostChat;
